import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    target?.addEventListener('change', this.#handleVariantChange, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    const selectedMedia = this.#resolveMediaSelection(this, [this.dataset.selectedMediaId]);
    if (selectedMedia.id) this.selectMedia(selectedMedia.id, selectedMedia.index);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        const selectedMedia = this.#resolveMediaSelection(newMediaGallery, [
          newMediaGallery.dataset.selectedMediaId,
          this.#selectedMediaIdFromDetail(detail),
        ], this.#selectedMediaGroupFromDetail(detail));
        this.#prepareInitialSlide(newMediaGallery, selectedMedia.index);
        this.replaceWith(newMediaGallery);

        this.#selectWhenReady(newMediaGallery, selectedMedia);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Immediately syncs the visible gallery when color-like variant controls change.
   *
   * @param {Event} event
   */
  #handleVariantChange = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.closest('product-card')) return;
    const picker = event.target.closest('variant-picker');
    if (!picker) return;

    const group = this.#selectedMediaGroupFromPicker(picker);
    if (!group) return;

    const selectedMedia = this.#resolveMediaSelection(this, [], group);
    if (selectedMedia.id) this.selectMedia(selectedMedia.id, selectedMedia.index);
  };

  /**
   * Gets the selected variant's media id from the product select response.
   *
   * @param {object} detail - The product select detail.
   * @returns {string}
   */
  #selectedMediaIdFromDetail(detail) {
    const variant = detail.variant || detail.resource;
    const featuredMediaId = variant?.featured_media?.id || variant?.featuredMedia?.id;
    return featuredMediaId ? String(featuredMediaId) : '';
  }

  /**
   * Resolves the selected media id against the actual slideshow slides.
   * Shopify variant payloads can expose either a product image id or a media id,
   * while Horizon slides are keyed by product media ids.
   *
   * @param {Element} mediaGallery
   * @param {Array<string | undefined>} candidateIds
   * @param {string} [fallbackGroup]
   * @returns {{id: string, index: number}}
   */
  #resolveMediaSelection(mediaGallery, candidateIds, fallbackGroup = '') {
    const slides = Array.from(mediaGallery.querySelectorAll('slideshow-slide[slide-id]'));

    for (const candidate of candidateIds) {
      if (!candidate) continue;

      const candidateId = String(candidate);
      const candidateTail = candidateId.split('/').pop();
      const matchingSlide = slides.find((slide) => {
        const slideId = slide.getAttribute('slide-id') || '';
        const slideTail = slideId.split('/').pop();

        return slideId === candidateId || slideTail === candidateId || slideId === candidateTail || slideTail === candidateTail;
      });

      if (matchingSlide) {
        return {
          id: matchingSlide.getAttribute('slide-id') || candidateId,
          index: Math.max(slides.indexOf(matchingSlide), 0),
        };
      }
    }

    if (fallbackGroup) {
      const matchingSlide = slides.find((slide) => slide.getAttribute('data-mark-media-group') === fallbackGroup);
      if (matchingSlide) {
        return {
          id: matchingSlide.getAttribute('slide-id') || '',
          index: Math.max(slides.indexOf(matchingSlide), 0),
        };
      }
    }

    return { id: '', index: 0 };
  }

  /**
   * Gets a color media group from variant data when Shopify media ids are not compatible.
   *
   * @param {object} detail - The product select detail.
   * @returns {string}
   */
  #selectedMediaGroupFromDetail(detail) {
    const variant = detail.variant || detail.resource;
    const values = [
      variant?.option1,
      variant?.option2,
      variant?.option3,
      variant?.title,
      variant?.name,
    ].filter(Boolean).join(' ').toLowerCase();

    if (values.includes('black')) return 'black';
    if (values.includes('gray') || values.includes('grey')) return 'gray';
    if (values.includes('khaki')) return 'khaki';
    if (values.includes('white')) return 'white';

    return '';
  }

  /**
   * Reads the currently selected variant controls and maps color values to media groups.
   *
   * @param {Element} picker
   * @returns {string}
   */
  #selectedMediaGroupFromPicker(picker) {
    const values = Array.from(picker.querySelectorAll('input:checked, select'))
      .map((control) => {
        if (control instanceof HTMLSelectElement) {
          return control.options[control.selectedIndex]?.textContent || control.value;
        }

        if (control instanceof HTMLInputElement) return control.value;

        return control.textContent || '';
      })
      .join(' ')
      .toLowerCase();

    if (values.includes('black')) return 'black';
    if (values.includes('gray') || values.includes('grey')) return 'gray';
    if (values.includes('khaki')) return 'khaki';
    if (values.includes('white')) return 'white';

    return '';
  }

  /**
   * Sets initial slide attributes before the new slideshow component upgrades.
   *
   * @param {Element} mediaGallery
   * @param {number} index
   */
  #prepareInitialSlide(mediaGallery, index) {
    const slideshow = mediaGallery.querySelector('slideshow-component');
    slideshow?.setAttribute('initial-slide', String(index || 0));
  }

  /**
   * Selects media after replacement, retrying until custom elements are upgraded.
   *
   * @param {Element} mediaGallery
   * @param {{id: string, index: number}} selectedMedia
   */
  #selectWhenReady(mediaGallery, selectedMedia) {
    let attempts = 0;
    const select = () => {
      attempts += 1;

      if (mediaGallery instanceof MediaGallery) {
        mediaGallery.selectMedia(selectedMedia.id, selectedMedia.index);
        return;
      }

      const slideshow = mediaGallery.querySelector('slideshow-component');
      if (slideshow?.select) {
        if (selectedMedia.id) slideshow.select({ id: selectedMedia.id }, undefined, { animate: false });
        else slideshow.select(selectedMedia.index || 0, undefined, { animate: false });
        return;
      }

      if (attempts < 8) requestAnimationFrame(select);
    };

    requestAnimationFrame(select);
  }

  /**
   * Selects a media slide after the gallery has been replaced.
   *
   * @param {string} mediaId - The selected media id.
   * @param {number} [index] - The selected media index.
   */
  selectMedia(mediaId, index = 0) {
    requestAnimationFrame(() => {
      if (mediaId) {
        this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
        return;
      }

      this.slideshow?.select(index || 0, undefined, { animate: false });
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
